// The cross-field write rules, in one file.
//
// KEEPERS ONLY. Every create and update SHAPE is generated
// (src/gen/client.ts) from backend/crm-api/openapi and re-exported by
// src/facade.ts. What is left here is the behaviour JSON Schema cannot express:
// rules that read two or more fields at once.
//
// A JSON Schema says what a field is. It cannot say "start_date_latest may not
// fall before start_date_earliest", "a contract_rate term line carries a rate
// and no amount", or "a milestone cadence carries milestones whose percentages
// add to 100". Those are relations between fields, and they are the rules a
// person actually gets wrong. The facade re-attaches each one to its generated
// schema with `superRefine`, so the SPA validates a form against the same rule
// the Kotlin server enforces on the write.
//
// Every rule below is typed STRUCTURALLY — `{ price_source?: …; rate_minor?: … }`
// rather than a named schema type — so it composes onto whichever generated
// schema carries those fields. That is what lets one rule serve the create
// shape, the update shape and the preview shape, and it is why a regenerated
// client does not require an edit here.
//
// The `*Problem` helpers are the same rules asked as a question rather than
// asserted: the SPA calls them on the RESULTING row so a dialog can disable
// Save and say why, before the request goes out. The rule and the question stay
// beside each other because they must never disagree.
/**
 * The date range must be ordered. Mirrors the deals_start_range_ordered CHECK
 * so a caller gets a per-field 400 naming the problem rather than a 500 with a
 * constraint name in it.
 */
function requireOrderedStartRange(value, ctx) {
    const { start_date_earliest: earliest, start_date_latest: latest } = value;
    if (earliest !== undefined && latest !== undefined && latest < earliest) {
        ctx.addIssue({
            code: "custom",
            path: ["start_date_latest"],
            message: "start_date_latest must be on or after start_date_earliest",
        });
    }
}
/**
 * A coordinate is a pair. Mirrors the `deals_customer_coords_together` CHECK,
 * and catches BOTH ways of half-supplying one: sending a latitude without
 * mentioning a longitude, and sending the pair with one of them null.
 *
 * Requiring them in the same request is what makes this decidable from the
 * request alone — unlike the country rule below, there is no stored-row half
 * for the server to check afterwards.
 */
function requirePairedCoordinates(value, ctx) {
    const sentLat = value.customer_lat !== undefined;
    const sentLng = value.customer_lng !== undefined;
    if (sentLat !== sentLng) {
        ctx.addIssue({
            code: "custom",
            path: [sentLat ? "customer_lng" : "customer_lat"],
            message: COORDINATES_TOGETHER,
        });
        return;
    }
    if (sentLat && sentLng && (value.customer_lat === null) !== (value.customer_lng === null)) {
        ctx.addIssue({
            code: "custom",
            path: [value.customer_lat === null ? "customer_lat" : "customer_lng"],
            message: COORDINATES_TOGETHER,
        });
    }
}
const COORDINATES_TOGETHER = "Set customer_lat and customer_lng together, or neither — half a coordinate cannot be plotted, " +
    "and a row carrying only one of them reads as located to anything that checks the other.";
const COUNTRY_REQUIRED = "customer_country is required when a region or a city is set — a subdivision with no country " +
    'cannot be rolled up ("ON" is Ontario or it is nothing), and the deal would land in the ' +
    "revenue-by-region report's unlocated bucket while visibly carrying a location.";
/**
 * The half of the location rule a partial PATCH cannot answer alone: what the
 * row will BE once the patch lands. Exported for the same reason
 * `termLineShapeProblem` is — the API's `validateUpdate` and a form that wants
 * to disable its own submit button should be applying one rule, not two
 * readings of one sentence.
 *
 * Returns the problem message, or null when the outcome is legal.
 */
export function customerLocationProblem(resulting) {
    const named = resulting.customer_region !== null || resulting.customer_city !== null;
    return named && resulting.customer_country === null ? COUNTRY_REQUIRED : null;
}
/** Cross-field rules for DealCreate, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function dealCreateRules(value, ctx) {
    requireOrderedStartRange(value, ctx);
    requirePairedCoordinates(value, ctx);
    // On create the request IS the resulting row, so the shared question answers
    // itself here.
    const problem = customerLocationProblem({
        customer_country: value.customer_country ?? null,
        customer_region: value.customer_region ?? null,
        customer_city: value.customer_city ?? null,
    });
    if (problem !== null) {
        ctx.addIssue({ code: "custom", path: ["customer_country"], message: problem });
    }
}
/**
 * `currency` is immutable. Every term line under a deal is implicitly
 * denominated in the deal's currency — there is no per-line currency column —
 * so flipping it would silently reinterpret every frozen invoice line beneath
 * it, turning $6,120.00 CAD of billing history into USD without touching a
 * single stored number. A deal in the wrong currency is re-created, not edited.
 *
 * `partner_id` and `agreement_id` stay mutable: moving a deal to a different
 * agreement is ordinary commercial reality, and a trigger still forces the
 * pair to be consistent — the agreement must be the partner's own or one of
 * its owners'. Setting `agreement_id` to null hands the deal back to whatever
 * its partner inherits.
 *
 * A PATCH that changes only one end of the start-date range is still checked
 * against the stored other end — that comparison needs the current row, so it
 * happens server-side; the refinement here only catches the both-in-one-request
 * case. The location's country rule is the same shape: setting a city on a deal
 * whose stored country is null has to be judged against the stored row, so
 * `customerLocationProblem` is applied server-side rather than restated here as
 * a rule that would reject the ordinary "add a city to an already-located deal"
 * patch.
 *
 * The COORDINATE rule is not like that, and is enforced here: the pair must
 * move together in one request, which makes the resulting values wholly
 * determined by the patch.
 */
/** Cross-field rules for DealUpdate, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function dealUpdateRules(value, ctx) {
    requireOrderedStartRange(value, ctx);
    requirePairedCoordinates(value, ctx);
}
// ---------------------------------------------------------------------------
// Term lines — the five clauses, and the relations between them.
//
// A term line is one clause of a deal's interpretation: which facts it reads
// (selector), how many units they carry (quantity), what a unit is worth
// (price), whose money it is (share) and which invoice it lands on (period).
// Four of the five clauses constrain each other, and every rule below mirrors
// a CHECK on `crm.term_lines` so the SPA refuses a shape the server would
// refuse — with a sentence instead of a constraint name.
// ---------------------------------------------------------------------------
const CONTRACT_NEEDS_RATE = "A contract_rate line states what one unit costs, so it carries rate_minor and no amount_minor.";
const FLAT_NEEDS_AMOUNT = "A flat_per_period line states one figure per period, so it carries amount_minor and no rate_minor.";
const FACT_AMOUNT_CARRIES_NEITHER = "A fact_amount line takes its figure from the facts, so it carries neither rate_minor nor amount_minor.";
const FLAT_COUNTS_THE_PERIOD = 'A flat_per_period line counts the period itself: leave the quantity empty and set the unit to "period".';
const DEAL_HAS_NO_RULE = "A deal-sourced line reads deal facts, and a deal fact already names its term line. It carries no selector and no quantity.";
const PROVIDER_COST_IS_PRICED = "A provider_cost line reads facts that already state the money, so its price source is fact_amount.";
const MILESTONES_PAIRED = "A milestone cadence carries a milestone list, and a milestone list needs the milestone cadence.";
const MILESTONE_PCT_TOTAL = "The milestone percentages must add to 100 — a plan that adds to less bills less than the line is worth, and one that adds to more bills the customer twice.";
const MILESTONE_SEQ_UNIQUE = "Each milestone has one position; a seq appears once in the plan.";
const WINDOW_ORDERED = "effective_to cannot fall before effective_from";
/** The tolerance the percentage total is judged at, in percentage points. */
const PCT_EPSILON = 0.0005;
function isSet(value) {
    return value !== null && value !== undefined;
}
/**
 * The price clause, judged against a COMPLETE line. Split out because the
 * create shape, the preview shape and a form's draft all state the price source
 * and both figures at once, while a PATCH may state only one of the three.
 */
function priceClauseProblem(value) {
    const hasRate = isSet(value.rate_minor);
    const hasAmount = isSet(value.amount_minor);
    switch (value.price_source) {
        case "contract_rate":
            if (!hasRate)
                return { field: "rate_minor", message: CONTRACT_NEEDS_RATE };
            if (hasAmount)
                return { field: "amount_minor", message: CONTRACT_NEEDS_RATE };
            return null;
        case "flat_per_period":
            if (!hasAmount)
                return { field: "amount_minor", message: FLAT_NEEDS_AMOUNT };
            if (hasRate)
                return { field: "rate_minor", message: FLAT_NEEDS_AMOUNT };
            if (isSet(value.quantity))
                return { field: "quantity", message: FLAT_COUNTS_THE_PERIOD };
            if (value.unit !== undefined && value.unit !== "period") {
                return { field: "unit", message: FLAT_COUNTS_THE_PERIOD };
            }
            return null;
        case "fact_amount":
            if (hasRate)
                return { field: "rate_minor", message: FACT_AMOUNT_CARRIES_NEITHER };
            if (hasAmount)
                return { field: "amount_minor", message: FACT_AMOUNT_CARRIES_NEITHER };
            return null;
        default:
            return null;
    }
}
function selectorClauseProblem(value) {
    if (value.source === "deal") {
        if (isSet(value.where))
            return { field: "where", message: DEAL_HAS_NO_RULE };
        if (isSet(value.quantity))
            return { field: "quantity", message: DEAL_HAS_NO_RULE };
    }
    if (value.source === "provider_cost" &&
        value.price_source !== undefined &&
        value.price_source !== "fact_amount") {
        return { field: "price_source", message: PROVIDER_COST_IS_PRICED };
    }
    return null;
}
function milestoneClauseProblem(value) {
    const milestones = value.milestones ?? null;
    if (value.cadence !== undefined && (value.cadence === "milestone") !== (milestones !== null)) {
        return { field: "milestones", message: MILESTONES_PAIRED };
    }
    if (milestones === null)
        return null;
    const seen = new Set();
    for (const milestone of milestones) {
        if (seen.has(milestone.seq)) {
            return { field: "milestones", message: MILESTONE_SEQ_UNIQUE };
        }
        seen.add(milestone.seq);
    }
    const total = milestones.reduce((sum, milestone) => sum + milestone.pct, 0);
    return Math.abs(total - 100) > PCT_EPSILON
        ? { field: "milestones", message: MILESTONE_PCT_TOTAL }
        : null;
}
function windowClauseProblem(value) {
    const from = value.effective_from;
    const to = value.effective_to;
    if (from === undefined || to === undefined || to === null)
        return null;
    return to < from ? { field: "effective_to", message: WINDOW_ORDERED } : null;
}
/**
 * A term line's shape, judged against the RESULTING line. Shared by the create
 * schema, the preview schema, the server's `validateUpdate` and the form that
 * wants to disable its own Save button — one rule, not four readings of it.
 *
 * Returns `{ field, message }` for the first problem, or null when legal.
 */
export function termLineShapeProblem(resulting) {
    return (selectorClauseProblem(resulting) ??
        priceClauseProblem(resulting) ??
        milestoneClauseProblem(resulting) ??
        windowClauseProblem(resulting));
}
function addProblem(problem, ctx) {
    if (problem !== null) {
        ctx.addIssue({ code: "custom", path: [problem.field], message: problem.message });
    }
}
/** Cross-field rules for TermLineCreate, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function termLineCreateRules(value, ctx) {
    // On create the request IS the resulting line, so the shared question
    // answers itself here.
    addProblem(termLineShapeProblem(value), ctx);
}
/**
 * A preview body carries a whole line — it is what the evaluator would run —
 * so it is judged exactly as a create is.
 */
/** Cross-field rules for TermLinePreview, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function termLinePreviewRules(value, ctx) {
    addProblem(termLineShapeProblem(value), ctx);
}
/**
 * `deal_id` is immutable: a line's currency is its deal's, and an issued
 * invoice line pins the line's revision — moving a line between deals would
 * leave a frozen figure describing a customer it was never billed to.
 *
 * A PATCH judges only what it carries. The rules that need the stored row are
 * finished by the server against the resulting line, with the same
 * `termLineShapeProblem`.
 */
/** Cross-field rules for TermLineUpdate, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function termLineUpdateRules(value, ctx) {
    // Only the halves a partial body can decide alone: the price clause needs
    // its own price_source in the body, and the milestone clause needs its own
    // cadence.
    if (value.price_source !== undefined)
        addProblem(priceClauseProblem(value), ctx);
    if (value.source !== undefined)
        addProblem(selectorClauseProblem(value), ctx);
    if (value.cadence !== undefined || value.milestones !== undefined) {
        addProblem(milestoneClauseProblem(value), ctx);
    }
    addProblem(windowClauseProblem(value), ctx);
}
// ---------------------------------------------------------------------------
// Prepaid entitlements — a pool, the window it covers and the lines it covers.
// ---------------------------------------------------------------------------
const POOL_WINDOW_ORDERED = "period_end cannot fall before period_start";
const POOL_COVERS_ONCE = "A term line appears once in the coverage list; naming it twice would allocate the pool to it twice.";
function poolProblem(value) {
    const { period_start: from, period_end: to } = value;
    if (from !== undefined && to !== undefined && to !== null && to < from) {
        return { field: "period_end", message: POOL_WINDOW_ORDERED };
    }
    const covered = value.covered_term_line_ids;
    if (covered !== undefined && new Set(covered).size !== covered.length) {
        return { field: "covered_term_line_ids", message: POOL_COVERS_ONCE };
    }
    return null;
}
/** Cross-field rules for PrepaidEntitlementCreate, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function prepaidEntitlementCreateRules(value, ctx) {
    addProblem(poolProblem(value), ctx);
}
/** Cross-field rules for PrepaidEntitlementUpdate, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function prepaidEntitlementUpdateRules(value, ctx) {
    addProblem(poolProblem(value), ctx);
}
// ---------------------------------------------------------------------------
// Invoice lines — the one place an interpreted figure is stored.
// ---------------------------------------------------------------------------
const SHARE_NEEDS_CHARGE = "A line cannot say what reaches us out of an amount it does not have. Clear our_share_minor too, or give the line a charge.";
const GROSS_NEEDS_CHARGE = "A line cannot say what the customer was charged when the line itself has no figure. Clear gross_minor too, or give the line a charge.";
const GROSS_IS_LINKED = "gross_minor is what a shared amount was a share OF. A standalone line — a credit, a goodwill adjustment, a late fee — is its own amount and was not divided out of anything.";
const GROSS_AGREES_WITH_SHARE = "The base and our share come out of the same money and point the same way. One of these is negative and the other is not.";
const LINE_NEEDS_WORDS = "A standalone line needs a description — it has no term line to take its label from.";
const CATEGORY_IS_STANDALONE = "A category says what KIND of charge a standalone line is. A term-line row's kind is whatever the line bills.";
/**
 * A line's shape, judged against the RESULTING row. Shared by the create schema,
 * the server's `validateUpdate` and any form that wants to disable its own
 * submit button — the same arrangement `termLineShapeProblem` has, and for the
 * same reason.
 */
export function invoiceLineShapeProblem(resulting) {
    const linked = (resulting.term_line_id ?? null) !== null;
    if (!linked && (resulting.description ?? null) === null) {
        return { field: "description", message: LINE_NEEDS_WORDS };
    }
    if (linked && (resulting.category ?? null) !== null) {
        return { field: "category", message: CATEGORY_IS_STANDALONE };
    }
    if ((resulting.charged_minor ?? null) === null && (resulting.our_share_minor ?? null) !== null) {
        return { field: "our_share_minor", message: SHARE_NEEDS_CHARGE };
    }
    // The base, under the same three rules the DB checks — and one the DB cannot:
    // a standalone line has no term line to have been a share of.
    const gross = resulting.gross_minor ?? null;
    if (gross !== null && (resulting.charged_minor ?? null) === null) {
        return { field: "gross_minor", message: GROSS_NEEDS_CHARGE };
    }
    if (gross !== null && !linked) {
        return { field: "gross_minor", message: GROSS_IS_LINKED };
    }
    const share = resulting.our_share_minor ?? null;
    if (gross !== null && share !== null && gross >= 0 !== share >= 0) {
        return { field: "gross_minor", message: GROSS_AGREES_WITH_SHARE };
    }
    return null;
}
/**
 * A line, which is either LINKED to a term line or STANDALONE.
 */
/** Cross-field rules for InvoiceLineCreate, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function invoiceLineCreateRules(value, ctx) {
    // On create the request IS the resulting row, so the shared rule applies
    // directly — except for the amount, which a linked line may legitimately
    // leave to the server to pre-fill.
    addProblem(invoiceLineShapeProblem({ ...value, charged_minor: value.charged_minor ?? 0 }), ctx);
}
/**
 * `invoice_id` is immutable: a line belongs to one document, and moving it would
 * change what two invoices say at once. Delete it and add it to the other —
 * which is legal on a draft and impossible on anything issued, correctly.
 *
 * The term-line link is immutable too. What a line IS gets decided when it is
 * added; re-pointing it would leave its frozen amounts describing something
 * else.
 */
/** Cross-field rules for InvoiceLineUpdate, exported so the facade can compose them onto the generated client schema (see facade.ts). */
export function invoiceLineUpdateRules(value, ctx) {
    // The half a partial PATCH can decide alone: clearing the charge while
    // leaving a share behind is a fraction of nothing. The other orderings need
    // the stored row, so the server finishes the check.
    if (value.charged_minor === null && (value.our_share_minor ?? null) !== null) {
        ctx.addIssue({ code: "custom", path: ["our_share_minor"], message: SHARE_NEEDS_CHARGE });
    }
    if (value.charged_minor === null && (value.gross_minor ?? null) !== null) {
        ctx.addIssue({ code: "custom", path: ["gross_minor"], message: GROSS_NEEDS_CHARGE });
    }
}
//# sourceMappingURL=inputs.js.map